// src/components/Auth.js
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PasskeyLogin from './Auth/PasskeyLogin';
import { setCurrentUser } from '../utils/auth';

const API_BASE = 'https://api.lssi-fin.com';

function Authorized() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";

  const loginClicked = async () => {
    setError("");

    if (!username || !password) {
      setError("Por favor ingresa tu usuario y contraseña.");
      return;
    }

    try {
      setLoading(true);
      const resp = await axios.post(
        `${API_BASE}/api/auth/login/`,
        { username, password },
      );

      const user = resp?.data?.user;
      if (!user) {
        setError("Usuario o contraseña inválidos.");
        return;
      }

      setCurrentUser(user);
      navigate(from, { replace: true });
    } catch (e) {
      console.error(e);
      setError("Usuario o contraseña inválidos.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => e.key === 'Enter' && loginClicked();

  const handlePasskeySuccess = (user) => {
    navigate(from, { replace: true });
  };

  return (
    <div className="lssi-auth-page" style={styles.page}>
      {/* Left — brand panel */}
      <aside className="lssi-brand-panel" style={styles.brandPanel} aria-hidden="true">
        <div className="lssi-brand-inner" style={styles.brandInner}>
          <div className="lssi-dotmatrix-wrap" style={styles.dotmatrix}>
            {/* Dot-matrix pattern inspired by the LSSI mark */}
            <DotMatrix />
          </div>
          <h1 className="lssi-brand-title" style={styles.brandTitle}>LSSI Fin</h1>
          <p className="lssi-brand-tagline" style={styles.brandTagline}>
            Plataforma de gestión financiera para consultoría Lean Six Sigma
          </p>

          <ul className="lssi-value-list" style={styles.valueList}>
            <li style={styles.valueItem}>
              <span style={styles.valueBullet} /> Portafolio de proyectos y utilidades
            </li>
            <li style={styles.valueItem}>
              <span style={styles.valueBullet} /> Reparto de comisiones y honorarios
            </li>
            <li style={styles.valueItem}>
              <span style={styles.valueBullet} /> Control multi-moneda con FX mensual
            </li>
          </ul>

          <p className="lssi-brand-footer" style={styles.brandFooter}>Lean Six Sigma Institute</p>
        </div>
      </aside>

      {/* Right — form */}
      <main className="lssi-form-panel" style={styles.formPanel}>
        <div className="lssi-form-card" style={styles.formCard} onKeyDown={onKeyDown}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Iniciar sesión</h2>
            <p style={styles.formSubtitle}>Ingresa tus credenciales para continuar</p>
          </div>

          {/* Form */}
          <div style={styles.form}>
            {/* Username */}
            <div style={styles.fieldGroup}>
              <label htmlFor="username" style={styles.label}>Usuario</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <input
                  id="username"
                  type="text"
                  placeholder="tu.usuario@empresa.com"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoFocus
                  autoComplete="username"
                  style={styles.input}
                />
              </div>
            </div>

            {/* Password */}
            <div style={styles.fieldGroup}>
              <label htmlFor="password" style={styles.label}>Contraseña</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeButton}
                  tabIndex={-1}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <svg style={{ width: 18, height: 18 }} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                  ) : (
                    <svg style={{ width: 18, height: 18 }} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={styles.error} role="alert">
                <svg style={{ width: 16, height: 16, flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Forgot password */}
            <div style={{ textAlign: 'right', marginTop: '-8px' }}>
              <Link to="/forgot-password" style={styles.forgotLink}>
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Login Button */}
            <button
              onClick={loginClicked}
              disabled={loading}
              className="lssi-login-btn"
              style={{
                ...styles.loginButton,
                ...(loading ? styles.loginButtonDisabled : {}),
              }}
            >
              {loading ? (
                <span style={styles.loadingContent}>
                  <span style={styles.spinner} />
                  Iniciando sesión...
                </span>
              ) : (
                "Iniciar sesión"
              )}
            </button>

            {/* Divider */}
            <div style={styles.divider}>
              <div style={styles.dividerLine}></div>
              <span style={styles.dividerText}>o continúa con</span>
              <div style={styles.dividerLine}></div>
            </div>

            {/* Passkey Login */}
            <PasskeyLogin onSuccess={handlePasskeySuccess} />
          </div>

          <p style={styles.footer}>
            © {new Date().getFullYear()} LSSI · Lean Six Sigma Institute
          </p>
        </div>
      </main>

      {/* Global styles: spinner, input focus, responsive collapse */}
      <style>{`
        @keyframes lssi-spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #9ca3af !important; }
        input:focus {
          outline: none;
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.12) !important;
          background-color: #ffffff !important;
        }
        .lssi-login-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(220, 53, 69, 0.35) !important;
        }
        .lssi-login-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        /* Stack the split-screen on narrow viewports */
        @media (max-width: 900px) {
          .lssi-auth-page {
            flex-direction: column !important;
          }
          .lssi-brand-panel {
            min-height: 220px !important;
            padding: 32px 24px !important;
          }
          .lssi-brand-inner {
            max-width: none !important;
          }
          .lssi-value-list, .lssi-brand-footer {
            display: none !important;
          }
          .lssi-brand-title {
            font-size: 1.75rem !important;
          }
          .lssi-brand-tagline {
            font-size: 0.9375rem !important;
          }
          .lssi-dotmatrix {
            width: 72px !important;
            height: 72px !important;
          }
          .lssi-dotmatrix-wrap {
            margin-bottom: 16px !important;
          }
          .lssi-form-panel {
            padding: 24px 20px 48px !important;
          }
          .lssi-form-card {
            box-shadow: none !important;
            padding: 8px 0 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

// Dot-matrix visual — a 6×6 grid with the LSSI red dots forming a mark
function DotMatrix() {
  const size = 6;
  // Rows of booleans: true = brand red, false = white/transparent tone.
  // Pattern is deliberately asymmetric to feel purposeful, echoing the LSSI logo.
  const pattern = [
    [0, 1, 1, 0, 0, 1],
    [1, 1, 0, 1, 1, 1],
    [0, 1, 1, 1, 0, 0],
    [1, 0, 0, 1, 1, 1],
    [1, 1, 1, 0, 1, 0],
    [0, 1, 0, 1, 1, 1],
  ];
  const dotSize = 12;
  const gap = 8;
  const total = size * dotSize + (size - 1) * gap;
  return (
    <svg
      viewBox={`0 0 ${total} ${total}`}
      width="112"
      height="112"
      className="lssi-dotmatrix"
      aria-hidden="true"
    >
      {pattern.map((row, r) =>
        row.map((on, c) => (
          <circle
            key={`${r}-${c}`}
            cx={c * (dotSize + gap) + dotSize / 2}
            cy={r * (dotSize + gap) + dotSize / 2}
            r={dotSize / 2}
            fill={on ? '#ffffff' : 'rgba(255,255,255,0.18)'}
          />
        ))
      )}
    </svg>
  );
}

const BRAND_RED = '#dc3545';
const BRAND_RED_DARK = '#a01e2b';

const styles = {
  page: {
    // Leave room for the fixed-top HeaderNav so the brand panel doesn't sit
    // underneath it. 68px matches the nav's current height (logo + padding).
    minHeight: 'calc(100vh - 68px)',
    marginTop: '68px',
    display: 'flex',
    background: '#f6f5f2',
  },
  brandPanel: {
    flex: '0 0 45%',
    background: `linear-gradient(155deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%)`,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 40px',
    position: 'relative',
    overflow: 'hidden',
  },
  brandInner: {
    maxWidth: '440px',
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  dotmatrix: {
    marginBottom: '32px',
  },
  brandTitle: {
    fontSize: '2.75rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    margin: '0 0 12px',
    lineHeight: 1.1,
  },
  brandTagline: {
    fontSize: '1.0625rem',
    lineHeight: 1.55,
    margin: '0 0 32px',
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 400,
    maxWidth: '380px',
  },
  valueList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  valueItem: {
    fontSize: '0.9375rem',
    color: 'rgba(255,255,255,0.92)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    letterSpacing: '0.005em',
  },
  valueBullet: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '1px',
    background: '#ffffff',
    flexShrink: 0,
  },
  brandFooter: {
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.7)',
    margin: 0,
    fontWeight: 500,
  },
  formPanel: {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 40px',
  },
  formCard: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    boxShadow: '0 10px 40px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04)',
    padding: '40px 36px 28px',
    border: '1px solid rgba(15, 23, 42, 0.04)',
  },
  formHeader: {
    marginBottom: '28px',
  },
  formTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 4px',
    letterSpacing: '-0.01em',
  },
  formSubtitle: {
    fontSize: '0.9rem',
    color: '#6b7280',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#374151',
    letterSpacing: '0.02em',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    width: '18px',
    height: '18px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '11px 40px 11px 38px',
    fontSize: '0.9375rem',
    border: '1.5px solid #e5e7eb',
    borderRadius: '10px',
    backgroundColor: '#f9fafb',
    color: '#111827',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    color: '#dc2626',
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  forgotLink: {
    fontSize: '0.8125rem',
    color: BRAND_RED,
    textDecoration: 'none',
    fontWeight: 500,
  },
  loginButton: {
    width: '100%',
    padding: '12px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: BRAND_RED,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.25)',
    letterSpacing: '0.02em',
  },
  loginButtonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  loadingContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'lssi-spin 0.6s linear infinite',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '4px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e5e7eb',
  },
  dividerText: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  footer: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginTop: '28px',
    marginBottom: 0,
  },
};

export default Authorized;
